// EOL/EOS API Client

import apiClient from '../../api/client';
import type {
  EndOfLifeProduct,
  PaginatedResponse,
  SearchCriteria,
  ProductRelation,
  CreateProductRequest,
  UpdateProductRequest,
  CreateRelationRequest,
} from './types';

const BASE_PATH = '/api/EolEos';

export const eolEosApi = {
  /**
   * Get paginated list of EOL/EOS products
   */
  getProducts: async (pageNumber: number = 1, pageSize: number = 30): Promise<PaginatedResponse<EndOfLifeProduct>> => {
    const response = await apiClient.get<EndOfLifeProduct[]>(`${BASE_PATH}`, {
      params: { pageNumber, pageSize },
    });

    // Extract pagination info from response headers
    const totalCount = parseInt(response.headers['x-total-count'] || '0');
    const currentPage = parseInt(response.headers['x-page-number'] || '1');
    const itemsPerPage = parseInt(response.headers['x-page-size'] || '30');

    return {
      data: response.data,
      pagination: {
        totalCount,
        currentPage,
        itemsPerPage,
        totalPages: Math.ceil(totalCount / itemsPerPage),
      },
    };
  },

  /**
   * Get a single product by ID
   */
  getProduct: async (id: number): Promise<EndOfLifeProduct> => {
    const response = await apiClient.get<EndOfLifeProduct>(`${BASE_PATH}/${id}`);
    return response.data;
  },

  /**
   * Get list of all manufacturers
   */
  getManufacturers: async (): Promise<string[]> => {
    const response = await apiClient.get<string[]>(`${BASE_PATH}/manufacturers`);
    return response.data;
  },

  /**
   * Search products with criteria
   */
  searchProducts: async (
    criteria: SearchCriteria,
    pageNumber: number = 1,
    pageSize: number = 30
  ): Promise<PaginatedResponse<EndOfLifeProduct>> => {
    const params = {
      ...criteria,
      pageNumber,
      pageSize,
    };

    const response = await apiClient.get<EndOfLifeProduct[]>(`${BASE_PATH}/search`, {
      params,
    });

    // Extract pagination info from response headers
    const totalCount = parseInt(response.headers['x-total-count'] || '0');
    const currentPage = parseInt(response.headers['x-page-number'] || '1');
    const itemsPerPage = parseInt(response.headers['x-page-size'] || '30');

    return {
      data: response.data,
      pagination: {
        totalCount,
        currentPage,
        itemsPerPage,
        totalPages: Math.ceil(totalCount / itemsPerPage),
      },
    };
  },

  /**
   * Create a new product
   */
  addProduct: async (product: CreateProductRequest): Promise<EndOfLifeProduct> => {
    const response = await apiClient.post<EndOfLifeProduct>(`${BASE_PATH}`, product);
    return response.data;
  },

  /**
   * Update an existing product
   */
  updateProduct: async (id: number, product: UpdateProductRequest): Promise<EndOfLifeProduct> => {
    const response = await apiClient.put<EndOfLifeProduct>(`${BASE_PATH}/${id}`, product);
    return response.data;
  },

  /**
   * Delete a product
   */
  deleteProduct: async (id: number): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  },

  /**
   * Get product relations
   */
  getProductRelations: async (productId: number): Promise<ProductRelation[]> => {
    const response = await apiClient.get<ProductRelation[]>(`${BASE_PATH}/products/${productId}/relations`);
    return response.data;
  },

  /**
   * Add product relation
   */
  addProductRelation: async (relation: CreateRelationRequest): Promise<ProductRelation> => {
    const { productId, ...relationData } = relation;
    const response = await apiClient.post<ProductRelation>(`${BASE_PATH}/products/${productId}/relations`, relationData);
    return response.data;
  },

  /**
   * Update product relation
   */
  updateProductRelation: async (
    productId: number,
    mspVendor: string,
    mspProdId: string,
    relationType: string
  ): Promise<ProductRelation> => {
    const response = await apiClient.put<ProductRelation>(
      `${BASE_PATH}/products/${productId}/relations/${mspVendor}/${mspProdId}`,
      { relationType }
    );
    return response.data;
  },

  /**
   * Delete product relation
   */
  deleteProductRelation: async (productId: number, mspVendor: string, mspProdId: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/products/${productId}/relations/${mspVendor}/${mspProdId}`);
  },

  /**
   * Upload CSV file for a specific vendor type
   */
  uploadFile: async (
    file: File, 
    vendorType: string, 
    onProgress?: (percent: number) => void
  ): Promise<EndOfLifeProduct[]> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<EndOfLifeProduct[]>(`${BASE_PATH}/upload`, formData, {
      params: { vendorType },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (event) => {
        if (event.total && onProgress) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          onProgress(percentComplete);
        }
      },
    });

    return response.data;
  },

  /**
   * Bulk save products to database
   */
  saveProducts: async (products: EndOfLifeProduct[]): Promise<string> => {
    const response = await apiClient.post<string>(`${BASE_PATH}/save`, products);
    return response.data;
  },
};
